/* Knurlhouse product page (Turntable). Option buttons -> variant, stage views, size guide drawer,
   and add to cart through Horizon's cart events so the bag count updates as usual.
   After a successful add, a compact "Added to your bag" panel shows the line with View bag (the cart
   page) and Checkout; it never opens a drawer, dims the page or takes focus. Buy now checks out the
   chosen ring on its own: a separate Shopify cart is created through the Storefront API and the
   browser goes to its checkout, so the bag is never emptied or changed (a /cart/<id>:1 permalink
   would replace it). */
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const STOREFRONT_API = '/api/2026-07/graphql.json';
const BUY_NOW_MUTATION = `mutation KhBuyNow($input: CartInput) {
  cartCreate(input: $input) { cart { checkoutUrl } userErrors { message } }
}`;
const ADDED_FOR = 7000; // ms the confirmation stays up when left alone
const stillMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

class KhtProduct extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    const data = this.querySelector('[data-variants]');
    this.variants = data ? JSON.parse(data.textContent) : [];
    this.form = this.querySelector('[data-kht-form]');
    this.idInput = this.form?.querySelector('input[name="id"]');
    this.atc = this.querySelector('[data-atc]');
    this.buyNow = this.querySelector('[data-buy-now]');
    this.err = this.querySelector('[data-atc-error]');
    this.status = this.querySelector('[data-atc-status]');
    this.busy = false;
    this.setupAdded();
    this.priceEl = this.querySelector('[data-price]');
    this.stage = this.querySelector('[data-stage]');
    this.ring = this.querySelector('.kht-spin .kht-ring');
    this.shots = [...this.querySelectorAll('[data-shot]')];
    this.photos = [...this.querySelectorAll('[data-photo]')];
    this.shotTitle = this.querySelector('[data-shot-title]');
    this.note = this.querySelector('[data-photo-note]');
    this.groups = [...this.querySelectorAll('[data-option]')];
    this.shot = Math.max(0, this.shots.findIndex((b) => b.getAttribute('aria-selected') === 'true'));

    this.form?.addEventListener('change', () => this.update(true));
    this.form?.addEventListener('submit', (e) => this.submit(e));
    this.buyNow?.addEventListener('click', () => this.checkout());
    // Back from checkout restores this page from the back/forward cache with Buy now still loading.
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      this.busy = false;
      this.buyNow?.removeAttribute('aria-busy');
      this.hideAdded();
      this.update(false);
    });
    this.shareBtn = this.querySelector('[data-share]');
    this.shareBtn?.addEventListener('click', () => this.share());
    this.shareBtn?.addEventListener('touchstart', () => {}, { passive: true }); // lets iOS show :active
    this.shots.forEach((b, i) => b.addEventListener('click', () => this.setShot(i)));
    this.groups.find((g) => g.hasAttribute('data-finish'))?.querySelectorAll('input')
      .forEach((input) => input.addEventListener('click', () => { if (input.checked) this.showFinish(input.value); }));

    // Swipe between photos on touch screens, arrow keys on the photo bar. Never auto-advances.
    if (this.stage && this.photos.length > 1) {
      let x0 = null, y0 = null;
      this.stage.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' || e.target.closest('[data-shot]')) return;
        x0 = e.clientX; y0 = e.clientY;
      });
      this.stage.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0, dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) this.step(dx < 0 ? 1 : -1);
      });
      this.stage.addEventListener('pointercancel', () => { x0 = null; });
      this.stage.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        this.step(e.key === 'ArrowRight' ? 1 : -1);
      });
      this.querySelector('[data-thumbs]')?.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        this.step(e.key === 'ArrowRight' ? 1 : -1);
        this.shots[this.shot]?.focus();
      });
    }

    this.querySelectorAll('[data-tab-btn]').forEach((btn) =>
      btn.addEventListener('click', () => {
        const i = btn.getAttribute('data-tab-btn');
        this.querySelectorAll('[data-tab-btn]').forEach((b) => b.setAttribute('aria-selected', String(b === btn)));
        this.querySelectorAll('[data-tab-panel]').forEach((p) => (p.hidden = p.getAttribute('data-tab-panel') !== i));
      })
    );

    this.setupZoom();

    const guide = this.querySelector('[data-guide]');
    if (guide) {
      this.querySelectorAll('[data-guide-open]').forEach((b) => b.addEventListener('click', () => guide.showModal()));
      guide.querySelectorAll('[data-guide-close]').forEach((b) => b.addEventListener('click', () => guide.close()));
      guide.addEventListener('click', (e) => { if (e.target === guide) guide.close(); });
    }
    this.update(false);
    // Finish links (?option_values=…&finish=Gold) select that finish on arrival (never a size) and open
    // on its dark photo. A ?variant= is the latest pick and wins, but Shopify renders option_values over
    // variant (with the finish's first size), so a URL carrying both is put right here and cleaned.
    const params = new URLSearchParams(location.search);
    const linked = params.has('variant') && this.variants.find((v) => String(v.id) === params.get('variant'));
    if (linked && (params.has('finish') || params.has('option_values'))) this.openVariant(linked);
    else if (params.has('finish') && !params.has('variant')) this.openFinish(params.get('finish'));
  }

  // Select every option of a variant, as if the customer had picked it.
  openVariant(v) {
    this.groups.forEach((g, gi) => {
      const input = [...g.querySelectorAll('input')].find((x) => x.value === v.options[gi]);
      if (input) input.checked = true;
    });
    this.update(true);
  }

  openFinish(value) {
    const group = this.groups.find((g) => g.hasAttribute('data-finish'));
    const input = group && [...group.querySelectorAll('input')].find((x) => x.value.toLowerCase() === String(value).toLowerCase());
    if (!input) return;
    if (!input.checked) {
      input.checked = true;
      this.update(false);
    }
    const dark = this.photos.findIndex((p) => p.classList.contains('kht-shot--editorial') && p.dataset.finish === input.value);
    if (dark > -1) this.setShot(dark);
    else this.showFinish(input.value);
  }

  // Full-screen photo viewer: tap/click the main photo. Phones pinch-zoom natively and swipe between
  // photos (scroll snap); mice click a photo to toggle a 2.2x zoom centred on the click point.
  setupZoom() {
    const dlg = this.querySelector('[data-zoom]');
    if (!dlg || !this.stage) return;
    const track = dlg.querySelector('[data-zoom-track]');
    const slides = [...dlg.querySelectorAll('[data-zoom-slide]')];
    const idx = dlg.querySelector('[data-zoom-index]');
    let down = null;
    const go = (i, smooth) => {
      const n = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[n].offsetLeft, behavior: smooth ? 'smooth' : 'instant' });
    };
    const current = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    const unzoom = () => slides.forEach((f) => f.classList.remove('is-zoomed'));
    track.addEventListener('scroll', () => {
      if (idx) idx.textContent = String(current() + 1).padStart(2, '0');
    }, { passive: true });
    this.stage.addEventListener('pointerdown', (e) => { down = { x: e.clientX, y: e.clientY, t: Date.now() }; });
    this.stage.addEventListener('click', (e) => {
      if (e.target.closest('[data-shot], [data-photo-note], [data-ridges-toggle]')) return; // Pause spin never opens the viewer
      if (down && (Math.abs(e.clientX - down.x) > 10 || Math.abs(e.clientY - down.y) > 10)) return; // a swipe, not a tap
      unzoom();
      dlg.showModal();
      go(this.shot, false);
    });
    dlg.querySelector('[data-zoom-close]')?.addEventListener('click', () => dlg.close());
    dlg.querySelectorAll('[data-zoom-step]').forEach((b) => b.addEventListener('click', () => { unzoom(); go(current() + Number(b.dataset.zoomStep), true); }));
    dlg.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); unzoom(); go(current() + (e.key === 'ArrowRight' ? 1 : -1), true); }
    });
    slides.forEach((f) => f.addEventListener('click', (e) => {
      if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const img = f.querySelector('img'); if (!img) return;
      const r = img.getBoundingClientRect();
      img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
      f.classList.toggle('is-zoomed');
    }));
    dlg.addEventListener('close', () => { unzoom(); const i = current(); if (i !== this.shot && this.photos[i]) this.setShot(i); });
  }

  selection() {
    return this.groups.map((g) => g.querySelector('input:checked')?.value ?? null);
  }

  match(sel) {
    return this.variants.find((v) => v.options.every((o, i) => o === sel[i])) || null;
  }

  update(userChange) {
    const sel = this.selection();
    // Mark values that have no available variant with the other current picks.
    this.groups.forEach((g, gi) => {
      g.querySelectorAll('input').forEach((input) => {
        const ok = this.variants.some((v) => v.available && v.options[gi] === input.value &&
          v.options.every((o, oi) => oi === gi || sel[oi] === null || sel[oi] === o));
        input.classList.toggle('is-unavailable', !ok);
      });
      const label = g.querySelector('[data-opt-label]');
      if (label) label.textContent = sel[gi] ? `${label.dataset.default}: ${sel[gi]}` : label.dataset.default;
      if (sel[gi] !== null) this.optError(g, false);
    });

    // Finish colours on the spinning ring.
    const finish = this.groups.find((g) => g.hasAttribute('data-finish'));
    const fInput = finish?.querySelector('input:checked');
    if (fInput && this.ring) this.ring.style.cssText = fInput.dataset.vars || '';

    const v = this.match(sel);
    if (this.idInput) this.idInput.value = v ? v.id : '';
    if (v) {
      if (this.priceEl) this.priceEl.textContent = v.price;
      if (!this.busy) this.setAtc(v.available ? `${this.atcLabel()} · ${v.price}` : this.atc.dataset.soldOut, !v.available);
    } else if (!this.busy) {
      // An option is still missing: the buttons stay usable and explain what's missing when clicked.
      this.setAtc(`${this.atcLabel()} · ${this.priceEl?.textContent || ''}`, false);
    }
    if (userChange) this.syncUrl(v, fInput);

    // Show the real photo of the chosen finish.
    if (userChange && fInput) this.showFinish(fInput.value);
    this.titleShot();
  }

  // The address always describes the current pick, so a reload or a shared link opens the same ring:
  // ?variant= once a size is chosen, otherwise the chosen finish's finish link (no size).
  syncUrl(v, fInput) {
    const url = new URL(location.href);
    ['variant', 'option_values', 'finish'].forEach((k) => url.searchParams.delete(k));
    if (v) url.searchParams.set('variant', v.id);
    else if (fInput?.dataset.valueId) {
      url.searchParams.set('option_values', fInput.dataset.valueId);
      url.searchParams.set('finish', fInput.value);
    }
    history.replaceState(history.state, '', url);
  }

  // Share builds the link from the pick itself: the product page plus ?variant= once a size is chosen,
  // otherwise the finish link. Never the canonical URL (phone share sheets swap it in, dropping the
  // finish), and never the visitor's own UTMs, ad click ids or preview parameters.
  // Touch screens open the native share sheet; everything else copies the link. A cancelled share is
  // not an error. If the share sheet is missing or refuses, the link is copied instead and the button
  // becomes Copy link.
  // Events (share_clicked, share_completed, share_link_copied) go to Shopify's customer events for a
  // web pixel to use later, under the shop's privacy settings. Nothing is sent from here.
  async share() {
    if (this.sharing || this.shareLocked || !this.shareBtn) return; // one share at a time, so nothing counts twice
    const sel = this.selection();
    const v = this.match(sel);
    const fGroup = this.groups.find((g) => g.hasAttribute('data-finish'));
    const fInput = fGroup?.querySelector('input:checked');
    const sizeAt = this.groups.findIndex((g) => g.querySelector('.kht-sizes'));
    const url = new URL(this.dataset.url || location.pathname, location.origin);
    if (v) url.searchParams.set('variant', v.id);
    else if (fInput?.dataset.valueId) {
      url.searchParams.set('option_values', fInput.dataset.valueId);
      url.searchParams.set('finish', fInput.value);
    }
    const details = { product_id: this.dataset.productId, product_title: this.dataset.title };
    if (fInput) details.finish = fInput.value;
    if (sizeAt > -1 && sel[sizeAt]) details.size = sel[sizeAt];
    if (v) details.variant_id = String(v.id);

    const native = this.shareMode !== 'copy' && typeof navigator.share === 'function' && matchMedia('(pointer: coarse)').matches;
    this.track('share_clicked', { ...details, method: native ? 'share_sheet' : 'copy_link' });
    this.sharing = true;
    try {
      if (native) {
        try {
          await navigator.share({ title: this.dataset.title, url: url.href });
          this.track('share_completed', details);
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return; // the customer closed the share sheet
          this.shareMode = 'copy';
        }
      }
      await this.copyLink(url.href, details);
    } finally {
      this.sharing = false;
    }
  }

  async copyLink(href, details) {
    let copied = false;
    try {
      await navigator.clipboard.writeText(href);
      copied = true;
    } catch (error) {
      // Older browsers, or a clipboard the page may not use: copy through a hidden text field.
      const field = document.createElement('textarea');
      field.value = href;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position: fixed; top: 0; left: -9999px; opacity: 0;';
      document.body.append(field);
      field.select();
      try { copied = document.execCommand('copy'); } catch (e) { copied = false; }
      field.remove();
    }
    if (!copied) {
      // Nothing worked without a fresh tap: offer Copy link, which the next tap tries again.
      this.shareMode = 'copy';
      this.shareLabel(this.shareBtn.dataset.copy);
      return;
    }
    this.track('share_link_copied', details);
    this.shareLabel(this.shareBtn.dataset.copied);
    this.shareBtn.classList.add('is-copied'); // no hover effect while it confirms
    // With a mouse, Link copied is a confirmation for its two seconds: clicks and Enter do nothing. It
    // stays focusable (aria-disabled, not disabled) so keyboard focus isn't lost. Phones are unchanged.
    if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.shareLocked = true;
      this.shareBtn.setAttribute('aria-disabled', 'true');
    }
    this.announce(this.shareBtn.dataset.copied);
    clearTimeout(this.shareTimer);
    this.shareTimer = setTimeout(() => {
      this.shareLocked = false;
      this.shareBtn.removeAttribute('aria-disabled');
      this.shareBtn.classList.remove('is-copied');
      this.shareLabel(this.shareMode === 'copy' && this.shareBtn.dataset.copy || this.shareBtn.dataset.label);
    }, 2000);
  }

  shareLabel(text) {
    const label = this.shareBtn?.querySelector('[data-share-label]');
    if (label && text) label.textContent = text;
  }

  // Shopify customer events (Shopify.analytics.publish): only web pixels the shop adds, with the
  // visitor's consent, ever see them. Analytics never gets in the way of sharing.
  track(name, data) {
    try { window.Shopify?.analytics?.publish?.(name, data); } catch (error) { /* ignore */ }
  }

  // The photo a finish's variants use (its clean studio shot). Also shown when the finish that is
  // already selected is clicked again, which fires no change event (the page opens on a stone shot).
  showFinish(value) {
    const finish = this.groups.find((g) => g.hasAttribute('data-finish'));
    if (!finish) return;
    const fi = this.groups.indexOf(finish);
    const v = this.match(this.selection());
    const withImg = (v && v.media && v.options[fi] === value) ? v : this.variants.find((x) => x.options[fi] === value && x.media);
    if (!withImg) return;
    const idx = this.photos.findIndex((p) => p.dataset.mediaId === String(withImg.media));
    if (idx > -1 && idx !== this.shot) this.setShot(idx);
  }

  // "Add Blue to cart": the button names the chosen finish, whichever photo is showing, so the colour
  // ordered is always the one selected. Plain label until a finish is chosen.
  atcLabel() {
    const finish = this.groups.find((g) => g.hasAttribute('data-finish'))?.querySelector('input:checked')?.value;
    return finish && this.atc.dataset.labelFinish ? this.atc.dataset.labelFinish.replace('[finish]', finish) : this.atc.dataset.label;
  }

  // `disabled` only ever means sold out; it applies to Buy now as well.
  setAtc(text, disabled) {
    if (!this.atc) return;
    this.atc.textContent = text;
    this.atc.disabled = disabled;
    if (this.buyNow) {
      this.buyNow.textContent = this.buyNow.dataset.label;
      this.buyNow.disabled = disabled;
    }
  }

  optError(group, show) {
    const msg = group.querySelector('[data-opt-error]');
    group.classList.toggle('is-nudge', show);
    if (msg) msg.hidden = !show;
    group.querySelectorAll('input').forEach((input) => {
      if (show && msg) input.setAttribute('aria-describedby', msg.id);
      else input.removeAttribute('aria-describedby');
    });
    if (show) group.setAttribute('aria-invalid', 'true');
    else group.removeAttribute('aria-invalid');
  }

  // Shared by Add to cart and Buy now. Every option needs a pick, and a size is never chosen for the
  // customer. A missing one gets its message, comes into view and takes focus. Returns the variant.
  ready() {
    const sel = this.selection();
    const missing = this.groups.filter((g, i) => sel[i] === null);
    if (missing.length) {
      missing.forEach((g) => this.optError(g, true));
      const first = missing[0];
      const box = first.getBoundingClientRect();
      // Jump, like the rest of the site (a smooth scroll here never moved the page in testing).
      if (box.top < 90 || box.bottom > window.innerHeight) first.scrollIntoView({ block: 'center', behavior: 'instant' });
      (first.querySelector('input:not(.is-unavailable)') || first.querySelector('input'))?.focus({ preventScroll: true });
      return null;
    }
    return this.match(sel);
  }

  showError(text) {
    if (!this.err) return;
    this.err.textContent = text;
    this.err.hidden = !text;
  }

  announce(text) {
    if (!this.status) return;
    this.status.textContent = '';
    setTimeout(() => { this.status.textContent = text; }, 50);
  }

  // Finish shown in the current photo: its data-finish (Knurlhouse photos, from the file name), else the
  // variant that uses it (null for photos of no single finish).
  photoFinish() {
    const group = this.groups.find((g) => g.hasAttribute('data-finish'));
    if (!group) return null;
    const tagged = this.photos[this.shot]?.dataset.finish;
    if (tagged) return tagged;
    const fi = this.groups.indexOf(group);
    const id = this.photos[this.shot]?.dataset.mediaId;
    const v = this.variants.find((x) => String(x.media) === id);
    return v ? v.options[fi] : null;
  }

  titleShot() {
    const group = this.groups.find((g) => g.hasAttribute('data-finish'));
    const chosen = group?.querySelector('input:checked')?.value || null;
    const shown = this.photoFinish();
    if (this.shotTitle) {
      const btn = this.shots[this.shot];
      this.shotTitle.textContent = [btn?.dataset.title || '01', shown].filter(Boolean).join(' · ');
    }
    // Never let a photo of one finish stand in silently for another.
    if (this.note && group) {
      const fi = this.groups.indexOf(group);
      const pictured = chosen && this.variants.some((x) => x.options[fi] === chosen && x.media);
      const mismatch = chosen && !pictured;
      this.note.hidden = !mismatch;
      if (mismatch) this.note.textContent = shown ? `${chosen} not pictured · photo shows ${shown}` : `${chosen} not pictured`;
    }
  }

  step(dir) {
    const next = Math.min(this.photos.length - 1, Math.max(0, this.shot + dir));
    if (next !== this.shot) this.setShot(next);
  }

  setShot(i) {
    this.shot = i;
    this.shots.forEach((b, j) => b.setAttribute('aria-selected', String(i === j)));
    const strip = this.querySelector('[data-thumbs]'), thumb = this.shots[i];
    if (strip && thumb) {
      const left = thumb.offsetLeft - strip.offsetLeft;
      if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({ left: left - 8, behavior: 'smooth' });
      }
    }
    this.photos.forEach((p, j) => p.classList.toggle('is-active', i === j));
    this.titleShot();
  }

  setBusy(button, text) {
    this.busy = Boolean(button);
    [this.atc, this.buyNow].forEach((b) => {
      if (!b) return;
      b.classList.toggle('is-busy', b === button);
      if (b === button) b.setAttribute('aria-busy', 'true');
      else b.removeAttribute('aria-busy');
    });
    if (button) button.textContent = text;
  }

  // The "Added to your bag" panel. The product section's container query (layout containment) would
  // pin a position: fixed panel to the section, so it opens as a manual popover (top layer, no backdrop,
  // no focus trap) and stays in the DOM right after Buy now, where Tab reaches it next. Browsers
  // without the Popover API get it moved to <body> instead.
  setupAdded() {
    const panel = this.querySelector('[data-added-panel]');
    if (!panel) return;
    this.added = panel;
    this.usePopover = typeof panel.showPopover === 'function';
    if (!this.usePopover) { panel.removeAttribute('popover'); document.body.append(panel); }
    this.addedTimer = null;
    this.addedHeld = false;
    const hold = () => { this.addedHeld = true; clearTimeout(this.addedTimer); };
    const release = () => { this.addedHeld = false; this.armAdded(); };
    panel.addEventListener('pointerenter', hold);
    panel.addEventListener('pointerleave', release);
    panel.addEventListener('focusin', hold);
    panel.addEventListener('focusout', (e) => { if (!panel.contains(e.relatedTarget)) release(); });
    panel.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.hideAdded(); });
    panel.querySelector('[data-added-close]')?.addEventListener('click', () => this.hideAdded());
  }

  armAdded() {
    clearTimeout(this.addedTimer);
    if (!this.addedHeld && this.added && !this.added.hidden) this.addedTimer = setTimeout(() => this.hideAdded(), ADDED_FOR);
  }

  // item: the line from /cart/add.js; v: the variant as shown on this page; count: bag item count.
  showAdded(item, v, count) {
    const panel = this.added;
    if (!panel) return;
    const parts = (item.options_with_values || []).filter((o) => o.value && o.value !== 'Default Title');
    this.addedSummary = parts.map((o) => `${o.name} ${o.value}`).join(', ');
    const img = panel.querySelector('[data-added-img]');
    const src = item.featured_image?.url || item.image;
    if (img) {
      // Our studio photos have clean square thumbnails for this panel (snippets/kh-thumb-names.liquid):
      // backdrop the panel's colour, ring a little larger. Anything else shows the product photo as before.
      let thumbs = {};
      try { thumbs = JSON.parse(img.dataset.thumbs || '{}'); } catch (e) { /* keep the photo */ }
      const name = src ? (new URL(src, location.origin).pathname.split('/').pop().split('-khstudio.')[0]) : '';
      const thumb = src && /-khstudio\./.test(src) ? thumbs[name] : null;
      img.classList.toggle('is-thumb', !!thumb);
      if (thumb) {
        img.src = thumb;
      } else if (src) {
        const url = new URL(src, location.origin);
        url.searchParams.set('width', '160');
        img.src = url.href;
      }
      img.alt = item.featured_image?.alt || item.product_title || '';
      img.hidden = !src;
    }
    panel.querySelector('[data-added-name]').textContent = item.product_title || '';
    // One line per option ("Finish — Silver Black"); the value never splits, so a long one moves
    // under its label as a whole.
    panel.querySelector('[data-added-opts]').replaceChildren(...parts.map((o) => {
      const line = document.createElement('p');
      const label = document.createElement('span');
      const value = document.createElement('span');
      line.className = 'kht-added__opt';
      label.textContent = `${o.name} —`;
      value.textContent = o.value;
      line.append(label, ' ', value);
      return line;
    }));
    panel.querySelector('[data-added-price]').textContent = v.price;
    panel.querySelector('[data-added-bag]').textContent = `View bag (${count})`;

    clearTimeout(this.addedOut);
    panel.classList.remove('is-out');
    if (panel.hidden) {
      panel.hidden = false;
      if (this.usePopover) panel.showPopover();
      // Next tick, so the entrance transition runs from the hidden state.
      setTimeout(() => panel.classList.add('is-in'), 20);
    } else {
      panel.classList.add('is-in');
    }
    this.armAdded();
  }

  hideAdded() {
    const panel = this.added;
    if (!panel || panel.hidden) return;
    clearTimeout(this.addedTimer);
    // Focus inside the panel would fall to <body>; give it back to the button that opened it.
    const hadFocus = panel.contains(document.activeElement);
    panel.classList.remove('is-in');
    panel.classList.add('is-out');
    this.addedHeld = false;
    this.addedOut = setTimeout(() => {
      if (this.usePopover && panel.matches(':popover-open')) panel.hidePopover();
      panel.hidden = true;
      panel.classList.remove('is-out');
    }, stillMotion() ? 0 : 220);
    if (hadFocus) this.atc?.focus({ preventScroll: true });
  }

  async submit(e) {
    e.preventDefault();
    if (this.busy) return; // a second click while adding never adds twice
    const v = this.ready();
    if (!v || !v.available) return;

    const formData = new FormData(this.form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: [{ merchandiseId: String(v.id), quantity: 1 }],
      promise: deferred.promise,
    }));

    this.setBusy(this.atc, this.atc.dataset.adding);
    this.showError('');
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cfg = fetchConfig('javascript', { body: formData });
      const res = await fetch(`${root}cart/add.js`, { ...cfg, headers: { ...cfg.headers, Accept: 'text/html' } }).then((r) => r.json());
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'kht-product', sourceId: this.dataset.section, itemCount: 1,
          productId: this.dataset.productId, sections: res.sections, didError: Boolean(res.status) },
      });
      if (res.status) {
        this.setBusy(null);
        this.update(false);
        this.showError(res.description || res.message || this.atc.dataset.failed);
        return;
      }
      this.atc.textContent = this.atc.dataset.added;
      this.showAdded(res, v, cart.item_count);
      this.announce(`Added to your bag: ${[res.product_title, this.addedSummary].filter(Boolean).join(', ')}.`);
      setTimeout(() => { this.setBusy(null); this.update(false); }, 1200);
    } catch (error) {
      deferred.reject(error);
      this.setBusy(null);
      this.update(false);
      this.showError(this.atc.dataset.failed);
    }
  }

  // Buy now: checkout for this ring alone, without touching the bag.
  async checkout() {
    if (this.busy || !this.buyNow) return;
    const v = this.ready();
    if (!v || !v.available) return;

    this.setBusy(this.buyNow, this.buyNow.dataset.busy);
    this.showError('');
    try {
      const quantity = Math.max(1, Number(this.form.querySelector('[name="quantity"]')?.value) || 1);
      const plan = this.form.querySelector('[name="selling_plan"]')?.value;
      const line = { merchandiseId: `gid://shopify/ProductVariant/${v.id}`, quantity };
      if (plan) line.sellingPlanId = `gid://shopify/SellingPlan/${plan}`;
      const input = { lines: [line] };
      const country = window.Shopify?.country;
      if (country) input.buyerIdentity = { countryCode: country };

      const res = await fetch(STOREFRONT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query: BUY_NOW_MUTATION, variables: { input } }),
      });
      const json = await res.json();
      const url = json?.data?.cartCreate?.cart?.checkoutUrl;
      if (!res.ok || !url) throw new Error(json?.data?.cartCreate?.userErrors?.[0]?.message || json?.errors?.[0]?.message || `HTTP ${res.status}`);
      window.location.assign(url);
    } catch (error) {
      console.warn('[kht-product] Buy now failed:', error);
      this.setBusy(null);
      this.update(false);
      this.showError(this.buyNow.dataset.failed);
    }
  }
}

if (!customElements.get('kht-product')) customElements.define('kht-product', KhtProduct);
